/**
 * Statutory Leave Calculation Utilities
 *
 * Calculates statutory minimum leave days based on working pattern.
 * German law: 4 days per workday per week (BUrlG § 3).
 */

/**
 * Counts workdays per week from days_distribution array.
 * A day is considered a workday if hours > 0.
 *
 * @param days - Array of 7 numbers representing hours per weekday [Mon, Tue, ..., Sun]
 * @returns Number of workdays per week (0-7)
 *
 * @example
 * workdaysPerWeek([8, 8, 8, 8, 8, 0, 0]) // → 5 (Mon-Fri)
 * workdaysPerWeek([6, 6, 6, 6, 0, 0, 0]) // → 4 (Mon-Thu)
 */
export function workdaysPerWeek(
  days: Array<number | null | undefined> | null | undefined
): number {
  if (!days || !Array.isArray(days)) return 0;
  return days.reduce((acc, h) => acc + (Number(h) > 0 ? 1 : 0), 0);
}

/**
 * Calculates statutory minimum leave days per year from working pattern.
 * Formula: workdays_per_week × 4 (German law: BUrlG § 3)
 *
 * @param days - Array of 7 numbers representing hours per weekday
 * @returns Statutory days per year, or null if no workdays
 *
 * @example
 * statutoryDaysPerYearFromPattern([8, 8, 8, 8, 8, 0, 0]) // → 20 (5-day week)
 * statutoryDaysPerYearFromPattern([8, 8, 8, 8, 0, 0, 0]) // → 16 (4-day week)
 * statutoryDaysPerYearFromPattern([0, 0, 0, 0, 0, 0, 0]) // → null
 */
export function statutoryDaysPerYearFromPattern(
  days: Array<number | null | undefined> | null | undefined
): number | null {
  const wd = workdaysPerWeek(days);
  if (wd <= 0) return null;
  return wd * 4;
}
